import { useState, useEffect, useCallback, useRef } from 'react';
import api from './useApi';

// Show-once behaviour for a FeatureTour. The server keeps one row per person
// per tour (feature_tour_progress): finished or skipped, either way it never
// auto-opens again, on any device. The page stays free to reopen it on demand
// via start() — a replay button — without disturbing the record until the
// person finishes or skips again.
//
// Usage, in the page that owns the tour:
//   const tour = useFeatureTour('desk-booking', { enabled: !loading });
//   ...
//   {tour.open && <FeatureTour steps={steps} onFinish={tour.finish} onSkip={tour.skip} />}
//
// `enabled` should be false until the page has rendered the elements the steps
// point at, so the tour does not open onto a loading spinner.
// A page may change WHICH tour it runs after its first render: Approvals
// picks between its ordinary tour and its "what changed" one from the
// person's start date, which arrives with them. The guard below therefore
// remembers the id it asked about rather than merely that it asked. As a
// bare boolean it swallowed exactly that case: the answer in flight for the
// old id was discarded when the id changed, the guard was already set, and
// nothing asked again, so no tour ever opened.
export function useFeatureTour(tourId, { enabled = true } = {}) {
  const [open, setOpen] = useState(false);
  const askedFor = useRef(null);

  useEffect(() => {
    if (!enabled || askedFor.current === tourId) return;
    // A tour open for the previous id must not stay open wearing the new
    // one's steps; whether the new one opens is the server's answer to give.
    if (askedFor.current !== null) setOpen(false);
    askedFor.current = tourId;
    let alive = true;
    let settled = false;
    api.get('feature-tours')
      .then(rows => {
        settled = true;
        if (alive && !(rows || []).some(r => r.tour_id === tourId)) setOpen(true);
      })
      // If the check fails, do nothing: wrongly staying quiet once beats
      // wrongly re-running the tour for everyone who has already seen it.
      .catch(() => { settled = true; });
    return () => {
      alive = false;
      // An ask that was never answered does not count as having asked. React
      // mounts, unmounts and remounts a component in development, and a
      // re-render can do the same in production: the first mount's answer is
      // discarded here, and leaving the guard set meant nothing ever asked
      // again, so the tour opened for nobody.
      if (!settled) askedFor.current = null;
    };
  }, [enabled, tourId]);

  const record = useCallback(
    (status) => api.put(`feature-tours/${tourId}`, { status }).catch(() => {}),
    [tourId],
  );

  return {
    open,
    start:  useCallback(() => setOpen(true), []),
    finish: useCallback(() => { setOpen(false); record('completed'); }, [record]),
    skip:   useCallback(() => { setOpen(false); record('skipped'); }, [record]),
  };
}

export default useFeatureTour;
